import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BackupPicker } from "@/components/tasks/BackupPicker";
import { fmtDue, fmtRange } from "@/components/tasks/OutOfOfficeDialog";
import { CalendarRange, AlertTriangle, ArrowRight, BellRing, Check, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const Tile = ({ label, value, cls = "", testid }) => (
  <div className="border border-border rounded-sm p-3" data-testid={testid}>
    <div className={`text-2xl font-condensed font-extrabold ${cls}`}>{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
  </div>
);

function DayGrid({ grid }) {
  return (
    <span className="inline-flex gap-0.5" title={DOW.filter((_, i) => grid[i]).join(", ")}>
      {grid.map((on, i) => <span key={DOW[i]} className={`h-3 w-3 rounded-[2px] ${on ? (i < 5 ? "bg-amber-500" : "bg-amber-300") : i < 5 ? "bg-secondary" : "bg-secondary/50"}`} />)}
    </span>
  );
}

function BackupCell({ e, onChanged }) {
  const [nudged, setNudged] = useState(!!e.backup_nudged_at);
  const nudge = async () => {
    try { await api.post(`/ooo/${e.id}/nudge-backup`); setNudged(true); toast.success(`Asked ${e.user_name} to name a backup`); }
    catch (err) { console.error("nudge", err); toast.error(err?.response?.data?.detail || "Could not send"); }
  };
  if (!e.backup_email) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded-sm bg-destructive/10 text-destructive" data-testid="coverage-no-backup"><AlertTriangle className="h-3 w-3" /> No backup</span>
        <BackupPicker entry={e} onSaved={onChanged} label="Set backup" testid={`coverage-set-backup-${e.id}`} />
        <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={nudged} onClick={nudge} data-testid={`coverage-nudge-${e.id}`}>
          {nudged ? <><Check className="h-3 w-3 mr-1" /> Nudged</> : <><BellRing className="h-3 w-3 mr-1" /> Ask them</>}
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      <span className="inline-flex items-center gap-1"><ArrowRight className="h-3 w-3 text-muted-foreground" /> {e.backup_name}</span>
      {e.backup_also_out && <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-destructive/10 text-destructive" data-testid="coverage-backup-out"><ShieldAlert className="h-3 w-3" /> Backup also out</span>}
      {e.backup_covers > 1 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800" data-testid="coverage-backup-load">covers {e.backup_covers}</span>}
      {e.backup_also_out && <BackupPicker entry={e} onSaved={onChanged} label="Change" testid={`coverage-set-backup-${e.id}`} />}
    </div>
  );
}

export function CoverageDialog({ open, setOpen, initialWeek = "next" }) {
  const [week, setWeek] = useState(initialWeek);
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/ooo/coverage", { params: { week } }).then((r) => setData(r.data)).catch((e) => console.error("coverage", e)), [week]);
  useEffect(() => { if (open) setWeek(initialWeek); }, [open, initialWeek]);
  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="coverage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarRange className="h-5 w-5 text-primary" /> Coverage report</DialogTitle>
          <DialogDescription>Who's out, who covers them, and where nobody is named — fill the gaps before Monday.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-sm border border-border overflow-hidden">
            {[["this", "This week"], ["next", "Next week"]].map(([k, l]) => (
              <button key={k} onClick={() => setWeek(k)} data-testid={`coverage-week-${k}`} className={`px-3 py-1.5 text-xs font-medium transition-colors ${week === k ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{l}</button>
            ))}
          </div>
          {data && <span className="text-sm text-muted-foreground" data-testid="coverage-range">{fmtRange(data.week_start, data.week_end)}</span>}
        </div>
        {!data ? <div className="h-40 animate-pulse bg-secondary/40 rounded-sm" /> : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Tile label="People out" value={data.totals.out} testid="coverage-out" />
              <Tile label="Coverage gaps" value={data.totals.gaps} cls={data.totals.gaps ? "text-destructive" : "text-emerald-600"} testid="coverage-gaps" />
              <Tile label="Busiest day" value={data.totals.busiest_day ? `${DOW[data.days.indexOf(data.totals.busiest_day)]} · ${data.totals.busiest_count}` : "—"} testid="coverage-busiest" />
            </div>
            <div className="grid grid-cols-7 gap-1" data-testid="coverage-days">
              {data.days.map((d, i) => (
                <div key={d} className={`rounded-sm border border-border p-2 text-center ${i >= 5 ? "opacity-60" : ""} ${data.per_day[i] ? "bg-amber-50 border-amber-200" : ""}`}>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{DOW[i]} {fmtDue(d).split(" ")[1]}</div>
                  <div className={`text-lg font-condensed font-extrabold ${data.per_day[i] ? "text-amber-800" : "text-muted-foreground/60"}`}>{data.per_day[i]}</div>
                </div>
              ))}
            </div>
            {data.entries.length === 0 ? (
              <p className="text-sm text-muted-foreground border border-dashed border-border rounded-sm p-6 text-center" data-testid="coverage-empty">No one is scheduled out {week === "next" ? "next week" : "this week"}.</p>
            ) : (
              <div className="divide-y divide-border border border-border rounded-sm">
                {data.entries.map((e) => (
                  <div key={e.id} className={`grid grid-cols-1 md:grid-cols-[1.3fr_1fr_1.4fr] gap-3 px-3 py-2.5 items-center ${e.gap ? "bg-destructive/[0.03]" : ""}`} data-testid="coverage-row" data-gap={e.gap ? "1" : "0"}>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{e.user_name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2"><DayGrid grid={e.grid} /> {fmtRange(e.start, e.end)} · back {fmtDue(e.back)}</div>
                      {e.note && <div className="text-[11px] text-muted-foreground truncate">{e.note}</div>}
                    </div>
                    <div className="text-xs text-muted-foreground" data-testid="coverage-load">
                      <span className={e.open_tasks ? "text-foreground font-medium" : ""}>{e.open_tasks} open task{e.open_tasks === 1 ? "" : "s"}</span> · <span className={e.open_tickets ? "text-foreground font-medium" : ""}>{e.open_tickets} ticket{e.open_tickets === 1 ? "" : "s"}</span>
                    </div>
                    <BackupCell e={e} onChanged={load} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
